import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft, CheckCircle2, Handshake, Hourglass, Loader2, Sparkles,
} from 'lucide-react';

/**
 * Catch-Up Missions — Parent read-only mission list (CUM-4).
 *
 * Routed at /parent/children/:cid/missions.
 *
 * Shows the parent a per-child view of:
 *   - completed missions (with topic + rationale quote)
 *   - in_progress missions (topic + last activity)
 *   - needs_help missions — topic withheld (privacy per locked decision B);
 *     framed as "Your teacher is following up."
 *
 * Skipped + expired missions are intentionally hidden.
 */
export default function ParentChildMissions() {
  const navigate = useNavigate();
  const { cid } = useParams();
  const [child, setChild] = useState(null);
  const [missions, setMissions] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Hydrate child name + the full mission list for this child.
      // The parent summary endpoint gives aggregate counts only; we use the
      // child detail + a filtered missions query via the child profile.
      const [cRes, mRes] = await Promise.all([
        api.get(`/children/${cid}`),
        api.get(`/parent/children/${cid}/missions`),
      ]);
      setChild(cRes.data);
      setMissions(mRes.data?.missions || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load missions');
    } finally {
      setLoading(false);
    }
  }, [cid]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="animate-spin text-indigo-600" />
      </div>
    );
  }

  const completed = missions.filter((m) => m.status === 'completed');
  const inProgress = missions.filter((m) => m.status === 'in_progress');
  const needsHelp = missions.filter((m) => m.status === 'needs_help');

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50 pb-16"
      data-testid="parent-child-missions-page"
    >
      <header className="border-b bg-white/95 backdrop-blur sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft size={14} className="mr-1" /> Back
          </Button>
          <div className="flex-1">
            <h1 className="font-bold text-slate-900 flex items-center gap-2">
              <Sparkles size={18} className="text-amber-500" />
              {child?.name ? `${child.name}'s` : 'Your child\'s'} catch-ups
            </h1>
            <p className="text-xs text-slate-500">
              Topics they explored to close gaps after their assessments
            </p>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8 space-y-6">
        {missions.length === 0 && (
          <div className="bg-white border-2 border-dashed border-slate-200 rounded-xl p-10 text-center" data-testid="empty-state">
            <CheckCircle2 size={36} className="mx-auto text-emerald-400 mb-3" />
            <p className="text-slate-700 font-medium">No catch-ups yet.</p>
            <p className="text-sm text-slate-500 mt-1">
              They'll appear after your child's next assessment.
            </p>
          </div>
        )}

        {completed.length > 0 && (
          <section className="space-y-2" data-testid="parent-completed-section">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-emerald-700/80 flex items-center gap-1.5">
              <CheckCircle2 size={12} /> Completed ({completed.length})
            </h2>
            {completed.map((m) => (
              <div
                key={m.mission_id}
                className="bg-white border border-emerald-200 rounded-xl p-4"
                data-testid={`parent-completed-${m.mission_id}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium text-slate-900">{m.concept_tag}</span>
                  <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-300">
                    +5
                  </Badge>
                </div>
                {m.completion_rationale && (
                  <blockquote className="border-l-2 border-amber-300 pl-2 mt-2 text-xs text-slate-600 italic">
                    {m.completion_rationale}
                  </blockquote>
                )}
              </div>
            ))}
          </section>
        )}

        {inProgress.length > 0 && (
          <section className="space-y-2" data-testid="parent-in-progress-section">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-amber-700/80 flex items-center gap-1.5">
              <Hourglass size={12} /> In progress ({inProgress.length})
            </h2>
            {inProgress.map((m) => (
              <div
                key={m.mission_id}
                className="bg-white border border-amber-200 rounded-xl p-4"
                data-testid={`parent-in-progress-${m.mission_id}`}
              >
                <div className="font-medium text-slate-900">{m.concept_tag}</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {m.exchanges_count || 0} turns so far
                </div>
              </div>
            ))}
          </section>
        )}

        {needsHelp.length > 0 && (
          <section className="space-y-2" data-testid="parent-needs-help-section">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-indigo-700/80 flex items-center gap-1.5">
              <Handshake size={12} /> Teacher follow-up ({needsHelp.length})
            </h2>
            {needsHelp.map((m) => (
              <div
                key={m.mission_id}
                className="bg-white border border-indigo-200 rounded-xl p-4"
                data-testid={`parent-needs-help-${m.mission_id}`}
              >
                <div className="font-medium text-slate-900">
                  A topic your teacher is following up on
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  Your child put in {m.exchanges_count || 0} turns of work · +2
                </div>
              </div>
            ))}
          </section>
        )}
      </main>
    </div>
  );
}
